import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { API } from '@app-core/constants/api.constants';
import { DataService } from '@app-core/services/data/data.service';
import { environment } from '@env/environment';
import { CLIENT_CONFIG } from '@config/config';
import { Router } from '@angular/router';
import { AccessService } from '@app-core/services/access/access.service';
import { LoginInfo } from '@app-core/models/core.model';
import { AmplifyAuthService } from './amplify-auth/amplify-auth.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  public loader = false;
  private defaultRedirectUrl = 'home';

  private tokenRefreshTimer;

  constructor(
    private http: HttpClient,
    private dataService: DataService,
    private router: Router,
    private accessService: AccessService,
    private amplifyAuth: AmplifyAuthService
  ) {}

  public intermediateServerLogin(params: any, headersData?: any): Observable<LoginInfo> {
    const awsCognitoRegion = this.accessService.getAwsCognitoRegion();
    let headers = {};
    if (headersData.token) {
      headers = new HttpHeaders({ Authorization: `Bearer ${headersData.token}` });
    }
    return this.http.post<LoginInfo>(API.INTERMEDIATE_SERVER_LOGIN, { ...params, region: awsCognitoRegion }, { headers });
  }

  public intermediateServerMasterLoginAs(params: any): Observable<LoginInfo> {
    return this.http.post<LoginInfo>(API.INTERMEDIATE_SERVER_MASTER_LOGIN_AS, params);
  }

  public intermediateServerAdminLoginAs(params: any): Observable<LoginInfo> {
    return this.http.post<LoginInfo>(API.INTERMEDIATE_SERVER_ADMIN_LOGIN_AS, params);
  }
  private isValidAbsoluteUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      return !!parsed.hostname; // Valid URL with hostname, not checking specific protocol to support companion app
    } catch {
      return false;
    }
  }

  public async logout(redirectUrl?: string | null) {
    if (environment.isGeotabEnv) return; // block this function if called in geotab environment
    if (CLIENT_CONFIG.showCustomLoginPage) {
      await this.amplifyAuth.logout(); //logout from amplify
    }

    this.dataService.userLogout.next();
    if (redirectUrl && this.isValidAbsoluteUrl(redirectUrl)) {
      window.location.href = redirectUrl;
      return;
    }
    if (environment.ssoAuthParams && environment.redirect_url) {
      window.location.href = environment.redirect_url;
    } else {
      this.redirectToLandingPage(true);
    }
  }

  private constructQueryParam(query: { [key: string]: any }) {
    return Object.entries(query)
      .filter(([, value]) => typeof value !== 'undefined')
      .map(([key, value]) => {
        if (Array.isArray(value) && value.length) {
          return value
            .filter((v) => typeof v !== 'undefined')
            .map((v) => `${key}[]=${v}`)
            .join('&');
        }
        return `${key}=${value}`;
      })
      .reduce((a, b) => `${a}&${b}`);
  }

  private async getAuthorizationParams(logout = false) {
    const awsCognitoRegion = this.accessService.getAwsCognitoRegion();
    const { domain, clientID } = environment.cognitoConfigs[awsCognitoRegion];
    if (logout && CLIENT_CONFIG.showLandingPage) {
      return {
        domain,
        client_id: clientID,
        logout_uri: environment.callbackURL.replace('/callback', ''),
      };
    }
    return {
      client_id: clientID,
      domain,
      response_type: 'code',
      scope: (environment.scopes || []).join('+'),
      redirect_uri: environment.callbackURL,
    };
  }
  public async authorizeUser() {
    if (CLIENT_CONFIG.showCustomLoginPage) {
      this.router.navigate(['/custom-login']);
    } else {
      const { domain, ...authorizationParams } = await this.getAuthorizationParams();
      window.location.href = `${API.oauth2Authorization(domain)}?${this.constructQueryParam(authorizationParams)}`;
    }
  }

  public async logoutUser() {
    if (CLIENT_CONFIG.showCustomLoginPage) {
      const callbackURL = environment.callbackURL.replace('/callback', '');
      this.router.navigate([callbackURL]);
    } else {
      const { domain, ...authorizationParams } = await this.getAuthorizationParams(true);
      window.location.href = `${API.oauth2Logout(domain)}?${this.constructQueryParam(authorizationParams)}`;
    }
  }

  public async redirectToLandingPage(logout = false) {
    if (logout) {
      this.logoutUser();
    } else if (CLIENT_CONFIG.showLandingPage && !CLIENT_CONFIG.showCustomLoginPage) {
      this.router.navigate(['/login']);
    } else {
      this.authorizeUser();
    }
  }

  public redirectLoggedInUser(redirectUrl = this.defaultRedirectUrl) {
    this.router.navigateByUrl(redirectUrl);
  }

  scheduleAccessTokenRefresh(expiresAtInMs: number): void {
    const now = Date.now();
    const refreshBuffer = 60 * 1000; // refresh 1 minute early
    const timeout = expiresAtInMs - now - refreshBuffer;

    // clear existing timer
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
    }

    if (timeout > 0) {
      this.tokenRefreshTimer = setTimeout(() => {
        this.refreshAccessToken();
      }, timeout);
    } else {
      // timer expired
      this.refreshAccessToken();
    }
  }

  refreshAccessToken(): Promise<any> {
    const awsCognitoRegion = this.accessService.getAwsCognitoRegion();

    const loginInfo = this.accessService.getLoginInfo();

    // user navigated to fleet portal via admin portal, do not schedule refresh token
    if (loginInfo.loginType == 'admin') {
      return Promise.resolve(true);
    }

    return new Promise((res) => {
      this.http.post<any>(API.refreshAccessToken, { region: awsCognitoRegion }).subscribe(
        (response) => {
          const { token, expiresAtInMs } = response;

          // scheduling token refresh timer
          this.scheduleAccessTokenRefresh(expiresAtInMs);

          // setting new access token in local storage
          loginInfo.token = token;
          loginInfo.tokenExpiresAt = expiresAtInMs;

          this.accessService.setLoginInfo(loginInfo);

          res(true);
        },
        (err) => {
          res(false);
          console.log(err);
          this.logout();
        }
      );
    });
  }

  public validateRedirection(token: any): Observable<LoginInfo> {
    const redirectionToken = token;
    return this.http.post<LoginInfo>(API.VALIDATE_REDIRECTION(redirectionToken), {});
  }
}
